import { Button } from '@/components/ui/button';
import useNetwork from '@/hooks/use-network';
import BackSpaceIcon from '@/icons/back-space-icon';
import {
  calculateDiscountPercent,
  formatCode,
  formatNumberToCurrency,
} from '@/lib/utils';
import { SoundContext } from '@/pages/(gapsdk)/_provider/sound-provider';
import { cn } from '@/utils/utils';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TopNavigation from '../../_components/top-navigation';

export default function GiftCardAddFundPage() {
  const maxValue = 1000000;
  const navigate = useNavigate();
  const { isOnline } = useNetwork();
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const { event: eventSound } = useContext(SoundContext);
  const {
    register,
    setValue: setFormValue,
    watch,
  } = useForm({
    defaultValues: {
      value: '',
      price: '',
    },
  });

  const id = searchParams.get('id');

  const [activeInputValue, setActiveInputValue] = useState('INPUT_VALUE');

  const { value, price } = watch();

  const discountPercentValue = calculateDiscountPercent(
    Number(value),
    Number(price),
  );

  const handleNumberClick = (num: string) => {
    const field = activeInputValue === 'INPUT_VALUE' ? 'value' : 'price';
    const currentValue = activeInputValue === 'INPUT_VALUE' ? value : price;
    const newValue = currentValue + num;
    setFormValue(field, newValue);
    if (activeInputValue === 'INPUT_VALUE') {
      setFormValue('price', newValue);
    }
  };

  const handleClear = () => {
    if (activeInputValue === 'INPUT_VALUE') {
      setFormValue('value', '');
      setFormValue('price', '');
    } else {
      setFormValue('price', '');
    }
  };

  const handleDelete = () => {
    const field = activeInputValue === 'INPUT_VALUE' ? 'value' : 'price';
    const currentValue = activeInputValue === 'INPUT_VALUE' ? value : price;
    if (Number(currentValue) === 0) {
      setFormValue(field, '');
      setFormValue('price', '');
    } else {
      setFormValue(field, currentValue.slice(0, -1));
      setFormValue('price', currentValue.slice(0, -1));
    }
  };

  const NUMERIC_BUTTONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 'C', 0, 'x'];

  return (
    <div className="flex h-full flex-col items-center justify-center p-24">
      <TopNavigation
        onBack={() => navigate(`/gift-card/information/${code}?id=${id}`)}
      />
      <div className="mx-auto size-full">
        <div className="grid size-full grid-cols-12 gap-12">
          <div className="col-span-7">
            <div className="size-full">
              <div className="flex size-full flex-col">
                <div className="flex-none">
                  <div className="mb-11">
                    <div className="mb-5 text-2xl font-normal">
                      Gift card value
                    </div>
                    <div
                      {...register('value')}
                      onClick={() => setActiveInputValue('INPUT_VALUE')}
                      className={cn(
                        'h-24 rounded-lg border-2 border-[#E3E3E333] bg-gray-50 text-5xl font-bold',
                        {
                          'border-primary': activeInputValue === 'INPUT_VALUE',
                        },
                      )}
                    >
                      <div
                        className={cn(
                          'flex size-full items-center px-8 text-gray-400',
                          { 'text-black': value },
                        )}
                      >
                        {value ? formatNumberToCurrency(Number(value)) : '$0'}
                      </div>
                    </div>
                    <div className="h-1 w-full">
                      <div className="pt-1 text-danger-discount">
                        {Number(value) >= maxValue &&
                          'The maximum value is $9,999.99.'}
                      </div>
                    </div>
                  </div>
                  <div className="mb-11">
                    <div className="mb-5 text-2xl font-normal">
                      Gift card price
                    </div>
                    <div
                      {...register('price')}
                      onClick={() => setActiveInputValue('INPUT_PRICE')}
                      className={cn(
                        'h-24 rounded-lg border-2 border-[#E3E3E333] bg-gray-50 text-5xl font-bold',
                        {
                          'border-primary': activeInputValue === 'INPUT_PRICE',
                        },
                      )}
                    >
                      <div className="flex size-full items-center justify-between px-8">
                        <div
                          className={cn('text-gray-400', {
                            'text-black': price,
                          })}
                        >
                          {price ? formatNumberToCurrency(Number(price)) : '$0'}
                        </div>
                        <div
                          className={cn('text-2xl text-red-400', {
                            hidden: !price,
                          })}
                        >
                          {discountPercentValue}% OFF
                        </div>
                      </div>
                    </div>
                    <div className="h-1 w-full">
                      <div className="pt-1 text-danger-discount">
                        {Number(price) >= maxValue &&
                          'The maximum value is $9,999.99.'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-normal">Gift card</div>
                    <div className="text-2xl font-bold">
                      {formatCode(code ?? '')}
                    </div>
                  </div>
                </div>

                <div className="grow" />

                <Button
                  disabled={
                    !value ||
                    !price ||
                    Number(value) === 0 ||
                    Number(value) >= maxValue ||
                    Number(price) >= maxValue ||
                    !isOnline
                  }
                  className="mt-6 h-24 w-full rounded-lg text-center text-2xl font-bold"
                  onClick={() => {
                    eventSound.playTap();
                    navigate(
                      `/gift-card/add-fund/${code}/review?price=${price}&balance_change=${value}&id=${id}`,
                    );
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>

          <div className="col-span-5 grid h-full grid-cols-3 gap-2">
            {NUMERIC_BUTTONS.map((num, i) => (
              <button
                key={i}
                className={
                  'click-effect flex h-full items-center justify-center rounded-lg border border-gray-200 bg-white text-5xl font-bold'
                }
                onMouseDown={() => {
                  eventSound.playTap();
                  if (num === 'C') {
                    handleClear();
                    return;
                  }
                  if (num === 'x') {
                    handleDelete();
                    return;
                  }
                  handleNumberClick(num.toString());
                }}
              >
                {num === 'x' ? (
                  <div className="flex items-center justify-center">
                    <BackSpaceIcon
                      style={{
                        width: 48,
                        height: 48,
                      }}
                    />
                  </div>
                ) : (
                  num
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
